import {Component,createEffect,createSignal,For,onMount, onCleanup, Show, JSX } from 'solid-js';
import { render } from "solid-js/web";
import {CMS} from './cms';
import {Resources} from './Data';
import {toQueryString,getPageRange,getPageBlockStart,getOrSetAsync, cleanImagePath} from './API';
import {Map} from './Map';
import { ArtworksGrid } from './ArtworksGrid';
import { AlphabeticalList, makeAccessor, PageList, SelectList, TextInput } from './Controls';
import { ArtworksBlocks } from './ArtworksBlocks';

type OrderBy = string | "ByTitle" | "ByDate" | "ByMedium" | "ByRandom" | "ByArtist" | "ByDateAdded" | "ByWidth" | "ByHeight";
type QueryState = "NotStarted" | "Loading" | "Success" | "Error";
type Layout = "Grid" | "Block" | "Map";
type ArtworkLocation = {
    id: number;
    name: string;
    details: string;
    url: string;
};
type Artwork = {
    id: number;
    showHiresIcon: boolean;
    alternateTitle: string;
    translateTitle: string;
    medium: string;
    measurements: string;
    credit: string;
    imageHeight: number;
    imageWidth: number;
    content: string;
    artworkLocation?: ArtworkLocation;
    imageCount: number;
    isForSale: boolean;
    isSold: boolean;
    dateRange: string;
    dateAdded: string;
    title: string;
    url: string;
    primaryImageUrl: string;
    backgroundColor: string;
};

type InfoWindowArgs = {
    name: string;
    artworks: any[];
};
function InfoWindow (p: InfoWindowArgs) {
  return (
    <>
        <div style="text-align: left; color: #333; max-width: 750px;">
            <h4>{p.name}</h4>
            <For each={p.artworks}>{(x: any) =>
                <div style="display: inline-block; padding: 5px; background-color: #333; text-align: center;">
                    <a href={ "/Artwork/Index/" + x.ArtworkId } target="_blank">
                        <img style="width: 100px; height: 100px;" src={ cleanImagePath(x.PrimaryImageUrl) + "?mode=crop&format=jpg&w=100&h=100" } />
                    </a>
                </div>
            }</For>
        </div>
    </>
  );      
};

export const ArtworksTab : Component<any> = (props) => {
    const [queryState, setQueryState] = createSignal<QueryState>("NotStarted");
    const [id, setId] = createSignal<number>(props.artistId);
    const [data, setData] = createSignal<{ markers: any[]; heatmap: any[] }|null>(null);
    const [layout, setLayout] = createSignal<Layout>("Grid");
    const [artworks, setArtworks] = createSignal<Artwork[]>([]);
    const [title, setTitle] = createSignal<string>("");
    const [type, setType] = createSignal<string>("");
    const [hasDepictedAddress, setHasDepictedAddress] = createSignal<string>("");
    const [medium, setMedium] = createSignal<string>("");
    const [country, setCountry] = createSignal<string>("");
    const [imageSize, setImageSize] = createSignal<string>("");
    const [startsWith, setStartsWith] = createSignal<string>("");
    const [startYear, setStartYear] = createSignal<string>("");
    const [endYear, setEndYear] = createSignal<string>("");
    const [order, setOrder] = createSignal<OrderBy>("ByDefault");
    const [direction, setDirection] = createSignal<OrderBy>("Ascending");
    const [page, setPage] = createSignal<number>(1);
    const [pages, setPages] = createSignal<number[]>([1]);
    const [letters, setLetters] = createSignal<string[]>([]);
    const [maxPage, setMaxPage] = createSignal<number>(1);
    const [mapMode, setMapMode] = createSignal<string>("markers");
    const [mapWidth, setMapWidth] = createSignal<string>("100%");
    const [mapHeight, setMapHeight] = createSignal<string>("600px");
    const [titleChars, setTitleChars] = createSignal<string[]>([]);
    const list = {
        types: makeAccessor(Resources.artworkTypes || []),
        countries: makeAccessor(Resources.countries || []),
        yesNos: makeAccessor(Resources.yesNos || []),
        imageSizes: makeAccessor([
            {Text:"", Value: ""},
            {Text:"Regular (up to 2 MPs)", Value: "M"},
            {Text:"Large (between 2 - 8 MPs)", Value: "L"},
            {Text:"Extra Large (between 8 - 16 MPs)", Value: "XL"},
            {Text:"Extremely Large (more than 16 MPs)", Value: "XXL"}
        ]),
        orderBy: makeAccessor([
            { Text: "By default", Value: "ByDefault" },
            { Text: "By title", Value: "ByTitle" },
            { Text: "By date", Value: "ByDate" },
            { Text: "By medium", Value: "ByMedium" },
            { Text: "By height", Value: "ByHeight" },
            { Text: "By width", Value: "ByWidth" },
            { Text: "By date added", Value: "ByDateAdded" },
            { Text: "By random artworks", Value: "ByRandom" }
        ])
    };
    
    const setMarkerMode = (e: Event) => {
        e.preventDefault();
        setMapMode("markers");
    }
    const setHeatmapMode = (e: Event) => {
        e.preventDefault();
        setMapMode("heatmap");
    }
    const resetFields = async () => {
        setTitle("");
        setCountry("");
        setType("");
        setMedium("");
        setStartYear("");
        setImageSize("");
        setHasDepictedAddress("");
        setStartsWith("");
        setEndYear(""),
        setPage(1);
        setMaxPage(1);
        setOrder("ByDefault");
        setDirection("Ascending");
        await search();
    };
    const pageBlockSize = 20;
    const skipToPreviousBlock = async () =>{
        const pageBlockStart = getPageBlockStart(page(), pageBlockSize);
        if (pageBlockStart - pageBlockSize >= 1) {
            const previousPageBlockStart = pageBlockStart - pageBlockSize;
            setPage(previousPageBlockStart);
            await search();
        }
    };
    const skipToNextBlock = async () =>{
        const pageBlockStart = getPageBlockStart(page(), pageBlockSize);
        if (pageBlockStart + pageBlockSize <= maxPage()) {
            const nextPageBlockStart = pageBlockStart + pageBlockSize;
            setPage(nextPageBlockStart);
            await search();
        }
    };
    const searchByStartLetter = async (letter: string) => {
        if (layout() === "Map") {
            setLayout("Grid");
        }
        setStartsWith(letter == startsWith() ? "" : letter);
        setPage(1);
        await search();
    };
    const searchByPage = async (page: number) => {
        if (layout() === "Map") {
            setLayout("Grid");
        }
        setPage(page);
        await search();
    }
    const submit = async (e: Event) => {
        e.preventDefault();
        await search();
    };
    const retrySearch = async () => {
        await search();
    };
    const sortByRecentArtworkAdditions = async () =>{
        setPage(1);
        setOrder("ByDateAdded");
        await search(true);
    }
    const sortByRandomArtworks = async () => {
        setPage(1);
        setOrder("ByRandom");
        await search(true);
    };  
    const getFilter = () => {
        const parameters = {
            ArtistId: id(),
            Title: title(),
            Medium: medium(),
            Country: country(),
            ArtworkType: type(),
            FromYear: startYear(),
            ToYear: endYear(),
            StartsWith: startsWith(),
            ImageSize: imageSize(),
            HasDepictedAddress: hasDepictedAddress(),
            Page: page(),
            Order: order(),
            Direction: direction()
        };
        return parameters;
    };
    const getData = async () => {
        const filter = toQueryString(getFilter());
        const json = await getOrSetAsync("artworks/" + filter, async () => {
            const response = await fetch("/Artist/SolidSearchArtworkJson?" + filter);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const search = async (reset = true) => {
        setQueryState("Loading");
        const json = await getData();
        if (!json.Success) {
            setQueryState("Error");
            return;
        }
        setQueryState("Success");
        const results: Artwork[] = json.Data.Items.map((item: any) => {
            const result: Artwork = {
                id: item.Id,
                backgroundColor: item.BackgroundColor,
                title: item.Title,
                alternateTitle: item.AlternateTitle,
                translateTitle: item.TranslatedTitle,
                medium: item.Medium,
                credit: item.Credit,
                content: item.Content,
                imageCount: item.ImageCount,
                measurements: item.Measurements,
                showHiresIcon: item.ShowHiresIcon,
                imageHeight: item.ImageHeight || 0,
                imageWidth: item.ImageWidth || 0,
                isForSale: item.IsForSale,
                isSold: item.IsSold,
                url: `/artworks/${item.Artist.Slug}/${item.Slug}/${item.Id}`,
                artworkLocation: !!item.ArtCollection ? {
                    id: item.ArtCollection.Id,
                    url: `/art-collections/${item.ArtCollection.Slug}/${item.ArtCollection.Id}`,
                    name: item.ArtCollection.Name,
                    details: [item.ArtCollection.City, item.ArtCollection.Country].filter(x => !!x).join(" | "),
                } : undefined,
                primaryImageUrl: item.ImageUrl,
                dateAdded: item.DateAdded,
                dateRange: item.DateRange
            };
            return result;
        });
        const _results = reset ? results : artworks().concat(results);
        setArtworks(_results);
        setMaxPage(json.Data.MaxPage);
        setTitleChars(json.Data.Titles);
        const pageRange = getPageRange(page(), pageBlockSize, maxPage());
        setPages(pageRange);
        return json;
    };
    const mapIconColour = () => layout() === "Map" ? "red" : "#000";
    const blockIconColour = () => layout() === "Block" ? "red" : "#000";
    const gridIconColour = () => layout() === "Grid" ? "red" : "#000";
    const markerLinkColour = () => mapMode() === "markers" ? "red" : "#efefef";
    const heatmapLinkColour = () => mapMode() === "heatmap" ? "red" : "#efefef";
    const loadNextPage = async () => {
        if (page() >= maxPage()) {
            return;
        }
        setPage(page() + 1);
        await search(false);
    };
    const createAlphabet = () => {
        const alpha = Array.from(Array(26)).map((e, i) => i + 65);
        const alphabet = alpha.map((x) => String.fromCharCode(x));
        return alphabet;
    };
    const getMapData = async ()  => {
        const json = await getOrSetAsync("map:markers-and-heatmap/" + props.artistId, async () => {
            const response = await fetch("/Artist/SolidMapOfArtworkLocationsJson?artistId=" + props.artistId);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const getInfoWindowData = async (params: any) => {
        const _params = toQueryString(params);
        const json = await getOrSetAsync("info-window/" + _params, async () =>{
            const response = await fetch("/Artwork/SolidGetArtworksAtGeolocation?" + _params);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const renderToString = (jsx: JSX.Element) => {
        const node = document.createElement("DIV");
        render(() => jsx, node);
        return node.innerHTML;
    };
    const onMarkerClicked = async (marker: google.maps.Marker, map: google.maps.Map) => {
        const latlng = marker.getPosition();
        const params = {
            artistId: props.artistId,
            latitude: latlng?.lat(),
            longitude: latlng?.lng()
        };
        const json = await getInfoWindowData(params);
        const artworks = json.Data;
        const model = {
            name: marker.data.Name,
            artworks
        };
        const jsx = InfoWindow(model);
        const content = renderToString(jsx);
        const infowindow = new google.maps.InfoWindow({ content });
        infowindow.open(map, marker);
    };
    const switchToMapLayout = async () => {
        console.log("switchToMapLayout");
        const json = await getMapData();
        const markers = json.Data.Markers;
        const heatmap = json.Data.Heatmap;
        console.log({ markers, heatmap });
        setData({ markers, heatmap });
        setLayout("Map");
    };
    const offset = () => layout() == "Grid" ? 1000 : layout() == "Block" ? 1250 : 500;
    const handleScroll = async () => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - offset()) {
            if (queryState() != "Loading") {
                await loadNextPage();
            }
        }
    };
    createEffect(async () => {
        window.addEventListener('scroll', handleScroll);
    });
    onCleanup(async () => {
        window.removeEventListener('scroll', handleScroll);
    });
    onMount(async () => {
        setLetters(createAlphabet());
        await search();
    });
return (
<div style="background-color: #fff;">   
    <form onSubmit={submit}>
        <section class="search" style="border: none; padding-bottom: 20px;">
            <div class="row">
                <div class="col-md-2">
                    <label>
                        <CMS id="label::title">Title</CMS>
                    </label>
                    <TextInput field={[title, setTitle]}></TextInput>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::medium">Medium</CMS>
                    </label>
                    <TextInput field={[medium, setMedium]}></TextInput>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::type">Type</CMS>
                    </label>
                    <SelectList field={[type, setType]} list={list.types}></SelectList>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::country">Country</CMS>
                    </label>
                    <SelectList field={[country, setCountry]} list={list.countries}></SelectList>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::sort-by">Sort by</CMS>
                    </label>
                    <SelectList field={[order, setOrder]} list={list.orderBy}></SelectList>
                </div>
            </div>
            <div class="row" style="margin-top: 10px;">
                <div class="col-md-2">
                    <div class="row">
                        <div class="col-md-6">
                            <label><CMS id="label::from-year">From year</CMS></label>
                            <TextInput field={[startYear, setStartYear]}></TextInput>
                        </div>
                        <div class="col-md-6">
                            <label><CMS id="label::to-year">To year</CMS></label>
                            <TextInput field={[endYear, setEndYear]}></TextInput>
                        </div>
                    </div>
                    <div class="row" style="margin-top: 10px;">
                        <div class="col-md-12">
                            <button style="margin-right: 5px;" type="button" class="btn btn-default" onClick={(e) => { e.preventDefault(); resetFields(); }}><CMS id="button::clear">Clear</CMS></button>
                            <button type="button" class="btn btn-primary" onClick={(e) => { e.preventDefault(); search(); }}><CMS id="button::search">Search</CMS></button>
                        </div>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Image size
                        </label>
                        <SelectList field={[imageSize, setImageSize]} list={list.imageSizes}></SelectList>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Depicts a place
                        </label>
                        <SelectList field={[hasDepictedAddress, setHasDepictedAddress]} list={list.yesNos}></SelectList>
                    </div>
                </div>
                <div class="col-md-4">
                    <label><CMS id="label::titles-starting-with">Titles starting with</CMS></label>
                    <div>
                        <AlphabeticalList letter={startsWith} existingLetters={titleChars} onClick={searchByStartLetter}></AlphabeticalList>
                    </div>
                    <label style="margin-top: 10px;"><CMS id="label::page">Page</CMS></label>
                    <div>
                        <span class="alphabet-index" onClick={() => skipToPreviousBlock() }><span class="fa fa-chevron-left"></span></span>
                        <PageList page={page} pages={pages} onClick={searchByPage}></PageList>
                        <span class="alphabet-index" onClick={() => skipToNextBlock() }><span class="fa fa-chevron-right"></span></span>
                    </div>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::view">View</CMS>
                    </label>
                    <div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Grid")}>
                            <div>
                                <span class="glyphicon glyphicon-th-large" style={{ "font-size": "2em", "color": gridIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": gridIconColour() }}>
                                <CMS id="label::grid">Grid</CMS>
                            </div>
                        </div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Block")}>
                            <div>
                                <span class="glyphicon glyphicon-th" style={{ "font-size": "2em", "color": blockIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": blockIconColour() }}>
                                <CMS id="label::block">Block</CMS>
                            </div>
                        </div>
                        <Show when={props.hasMapLocations}>
                            <div onClick={(e) => switchToMapLayout()}>
                                <div>
                                    <span class="glyphicon glyphicon-map-marker" style={{ "font-size": "2em", "color": mapIconColour() }}></span>
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": mapIconColour() }}>
                                    <CMS id="label::map">Map</CMS>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </div>
        </section>
    </form>
    <section class="salon-results">
        <div style="background-color: #333; position: relative; text-align: center;">
            <Show when={queryState() == "Success" && artworks().length === 0}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <h2 style="font-size: 3em; color: #fff; padding: 40px; font-family: 'Oswald';">
                            <CMS id="message::search-returned-no-results">This search returned no results.</CMS>
                        </h2>
                    </div>
                </div>
            </Show>
            <Show when={queryState() == "Error"}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <div style="padding: 40px;">
                            <h2 style="font-size: 3em; color: #fff; font-family: 'Oswald';">
                                <CMS id="message::unexpected-search-error">There was an unexpected error. Please try again.</CMS>
                            </h2>
                            <div style="font-size: 2em; margin-top: 30px;">
                                <button type="button" onClick={retrySearch}><span class="fa fa-refresh"></span> <CMS id="button::retry">Retry</CMS></button>
                            </div>
                        </div>
                    </div>
                </div>
            </Show>
            <Show when={layout() === "Grid"}>
                <ArtworksGrid isLoggedIn={props.isLoggedIn} artworks={artworks}></ArtworksGrid>                       
            </Show>
            <Show when={layout() === "Block"}>
                <ArtworksBlocks isLoggedIn={props.isLoggedIn} artworks={artworks} />
            </Show>
            <Show when={layout() === "Map"}>
                <div style="color: #efefef; text-align: left;">
                    <div style="margin: 15px 0; padding: 1px 20px;">
                        <h3 style="font-family: 'Impact'; letter-spacing: 0.15rem; padding-bottom: 10px;">
                            Location of artworks in museums &amp; collections
                        </h3>
                        <div>
                            <div onClick={(e) => setMarkerMode(e) } style="display: inline-block; margin-right: 20px;">
                                <div style="position: relative; left: 15px;">
                                    <span class="glyphicon glyphicon-map-marker" style={{ "font-size": "2em", "color": mapIconColour() }}></span>
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": markerLinkColour() }}>
                                    <CMS id="label::map">markers</CMS>
                                </div>
                            </div>
                            <div onClick={(e) => setHeatmapMode(e) } style="display: inline-block;">
                            <div style="position: relative; left: 15px; top: -5px;">
                                    <img src="/images/icons/heatmap.png" style="height: 30px; width: 30px;" />
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": heatmapLinkColour() }}>
                                    <CMS id="label::map">heatmap</CMS>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p style="color: #efefef; border-top: 2px dotted #efefef; padding: 10px 20px; margin: 0; font-size: 1.5em;" onClick={() => setLayout("Grid") }>
                        <span class="fa fa-arrow-circle-left" style="margin-right: 10px;"></span> <CMS id="button::back-to-the-artworks">back to the artworks</CMS>
                    </p>
                </div>
                <Map
                    width={mapWidth} height={mapHeight}
                    data={data}
                    onMarkerClicked={onMarkerClicked}
                    mode={mapMode}
                />
            </Show>
        </div>
    </section>
</div>);
}